// Variable substitution utility for terms and conditions
import { getCurrencySymbol } from "$lib/utils/currencySymbol"

export interface VariableDefinition {
  key: string
  label: string
  description: string
  value: (competition: any) => string
}

// Define available variables for terms and conditions
export const availableVariables: VariableDefinition[] = [
  {
    key: "competition_title",
    label: "Competition Title",
    description: "The name of the competition",
    value: (competition) => competition?.title || "[Competition Title]",
  },
  {
    key: "competition_slug",
    label: "Competition Slug",
    description: "The URL-friendly identifier for the competition",
    value: (competition) => competition?.slug || "[Competition Slug]",
  },
  {
    key: "start_date",
    label: "Start Date",
    description: "When the competition starts",
    value: (competition) => {
      if (competition?.startDate) {
        return new Date(competition.startDate).toLocaleString()
      }
      return "[Start Date]"
    },
  },
  {
    key: "end_date",
    label: "End Date",
    description: "When the competition ends",
    value: (competition) => {
      if (competition?.endDate) {
        return new Date(competition.endDate).toLocaleString()
      }
      return "[End Date]"
    },
  },
  {
    key: "start_date_short",
    label: "Start Date (Short)",
    description: "Start date in short format",
    value: (competition) => {
      if (competition?.startDate) {
        return new Date(competition.startDate).toLocaleDateString()
      }
      return "[Start Date]"
    },
  },
  {
    key: "end_date_short",
    label: "End Date (Short)",
    description: "End date in short format",
    value: (competition) => {
      if (competition?.endDate) {
        return new Date(competition.endDate).toLocaleDateString()
      }
      return "[End Date]"
    },
  },
  {
    key: "timezone",
    label: "Timezone",
    description: "The competition timezone",
    value: (competition) => competition?.timezone?.label || "[Timezone]",
  },
  {
    key: "sponsor_name",
    label: "Sponsor Name",
    description: "Name of the competition sponsor",
    value: (competition) =>
      competition?.termsAndRules?.sponsorName || "[Sponsor Name]",
  },
  {
    key: "sponsor_address",
    label: "Sponsor Address",
    description: "Address of the competition sponsor",
    value: (competition) =>
      competition?.termsAndRules?.sponsorAddress || "[Sponsor Address]",
  },
  {
    key: "governing_law_country",
    label: "Governing Law Country",
    description: "The country whose laws govern the competition",
    value: (competition) =>
      competition?.termsAndRules?.governingLawCountry ||
      "[Governing Law Country]",
  },
  {
    key: "winner_selection_method",
    label: "Winner Selection Method",
    description: "How winners will be selected",
    value: (competition) =>
      competition?.termsAndRules?.winnerSelectionMethod ||
      "[Winner Selection Method]",
  },
  {
    key: "contact_winner_days",
    label: "Contact Winner Days",
    description: "Days within which winners will be contacted",
    value: (competition) =>
      competition?.termsAndRules?.contactWinnerWithinDays?.toString() ||
      "[Contact Winner Days]",
  },
  {
    key: "claim_prize_days",
    label: "Claim Prize Days",
    description: "Days within which winners must claim prizes",
    value: (competition) =>
      competition?.termsAndRules?.claimPrizeWithinDays?.toString() ||
      "[Claim Prize Days]",
  },
  {
    key: "privacy_policy_url",
    label: "Privacy Policy URL",
    description: "Link to the privacy policy",
    value: (competition) =>
      competition?.termsAndRules?.privacyPolicyUrl || "[Privacy Policy URL]",
  },
  {
    key: "prize_list",
    label: "Prize List",
    description: "List of all prizes",
    value: (competition) => {
      if (competition?.prizes?.length) {
        return competition.prizes
          .map((prize: any, index: number) => {
            if (prize.value) {
              const currency = prize.currency || "USD"
              const symbol = getCurrencySymbol(currency)
              return `${index + 1}. ${prize.name} (${symbol}${prize.value})`
            }
            return `${index + 1}. ${prize.name}`
          })
          .join("<br>")
      }
      return "[Prize List]"
    },
  },
  {
    key: "total_prizes",
    label: "Total Prize Count",
    description: "Total number of prizes",
    value: (competition) => {
      if (competition?.prizes?.length) {
        return competition.prizes
          .reduce(
            (total: number, prize: any) => total + (prize.winners || 1),
            0,
          )
          .toString()
      }
      return "[Total Prize Count]"
    },
  },
  {
    key: "competition_url",
    label: "Competition URL",
    description: "Direct link to the competition",
    value: (competition) => {
      if (competition?.slug) {
        return `https://www.promoamp.com/c/${competition.slug}`
      }
      return "[Competition URL]"
    },
  },
]

/**
 * Replace variables in text with actual values
 * Variables should be in the format {{variable_key}}
 */
export function substituteVariables(text: string, competition: any): string {
  if (!text || typeof text !== "string") return text

  let result = text

  // Replace each variable
  availableVariables.forEach((variable) => {
    const pattern = new RegExp(`\\{\\{\\s*${variable.key}\\s*\\}\\}`, "gi")
    const value = variable.value(competition)
    result = result.replace(pattern, value)
  })

  return result
}

/**
 * Get a preview of available variables with their current values
 */
export function getVariablePreview(
  competition: any,
): { key: string; label: string; value: string; description: string }[] {
  return availableVariables.map((variable) => ({
    key: variable.key,
    label: variable.label,
    value: variable.value(competition),
    description: variable.description,
  }))
}

/**
 * Insert a variable into text at a specific position
 */
export function insertVariable(
  text: string,
  variableKey: string,
  position: number,
): string {
  const variableText = `{{${variableKey}}}`
  return text.slice(0, position) + variableText + text.slice(position)
}
